// Global
import React from 'react';

// Lib
import { UserProfile } from 'src/.generated/UserProfile/MyAccount.model';
import { Data } from '@generated/Foundation.GMI.model';
import { Field, Item, TextField } from '@sitecore-content-sdk/nextjs';
import MyAccount, { TabFields, TabId, TabItem } from './MyAccount';
import { usePlainTextWrapper } from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useIsEditing from 'lib/hooks/sitecore/useIsEditing';
import { PlaceholderWrapper } from 'components/helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import LinkWrapper, {
  useLinkWrapper,
} from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { useRichTextWrapper } from 'components/helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';
import useDictionary from 'lib/hooks/useDictionary';
import { Session } from 'next-auth';
import { SiteSettings } from 'lib/page-props-factory/plugins/site-settings';
import { useSessionSafe } from 'lib/hooks/auth/useSessionSafe';
import { getQuestionOptions } from 'lib/utils/question-options';

type MyAccountSitecoreProps = UserProfile.MyAccount.MyAccount_Component;

type TabFieldMap = TabFields;

// Helper component to convert password tab fields to ReactNodes
const PasswordTabFields = ({
  passwordFields,
}: {
  passwordFields: UserProfile.MyAccount.Password['fields'] | undefined;
}): Record<string, React.ReactNode | string | undefined> | undefined => {
  const editPasswordTitle = usePlainTextWrapper({
    field: passwordFields?.editPasswordTitle,
  });
  const description = usePlainTextWrapper({
    field: passwordFields?.description,
  });
  const editPasswordDescription = usePlainTextWrapper({
    field: passwordFields?.editPasswordDescription,
  });
  const currentPasswordLabel = usePlainTextWrapper({
    field: passwordFields?.updateCurrentPasswordLabel,
  });
  const newPasswordLabel = usePlainTextWrapper({
    field: passwordFields?.passwordLabel,
  });
  const confirmPasswordLabel = usePlainTextWrapper({
    field: passwordFields?.confirmPasswordLabel,
  });
  const saveButtonText = usePlainTextWrapper({
    field: passwordFields?.passwordCTALabel,
  });
  const successMessage = usePlainTextWrapper({
    field: passwordFields?.successfulUpdateMessage,
  });

  const leavePageTitle = usePlainTextWrapper({
    field: passwordFields?.confirmationTitle,
  });
  const leavePageDescription = usePlainTextWrapper({
    field: passwordFields?.confirmationDescription,
  });
  const passwordNotSupportedText = usePlainTextWrapper({
    field: passwordFields?.passwordNotSupportedText,
  });

  return {
    editPasswordTitle,
    description,
    editPasswordDescription,
    currentPasswordLabel,
    newPasswordLabel,
    confirmPasswordLabel,
    saveButtonText,
    successMessage,
    leavePageTitle,
    leavePageDescription,
    passwordNotSupportedText,
  };
};

const EmailSubscriptionTabFields = ({
  emailSubscriptionFields,
  session,
  siteSettings,
  getDictionaryValue,
}: {
  emailSubscriptionFields: UserProfile.MyAccount.EmailSubscriptions['fields'] | undefined;
  session: Session | null;
  siteSettings: SiteSettings | null;
  getDictionaryValue: (key: string) => string;
}): Record<string, React.ReactNode | string | undefined> | undefined => {
  const newsLetterSubscriptionId = siteSettings?.subscriptionID?.value;

  const isSubscribed = session?.user?.subscriptions?.find(
    (subscription) =>
      subscription.SubscriptionId === newsLetterSubscriptionId && subscription.Subscribed
  );

  return {
    sectionTitle: usePlainTextWrapper({ field: emailSubscriptionFields?.sectionTitle }),
    sectionDescription: useRichTextWrapper({ field: emailSubscriptionFields?.sectionDescription }),
    description: useRichTextWrapper({
      field: emailSubscriptionFields?.description,
      tokens: [
        {
          key: '${subscriptionToUnsubscribeFrom}',
          value: isSubscribed
            ? getDictionaryValue('Components.MyAccount.UnsubscribeFrom')?.toLowerCase()
            : getDictionaryValue('Components.MyAccount.SubscribeTo')?.toLowerCase(),
        },
      ],
    }),
    errorMessage: emailSubscriptionFields?.errorMessage?.value || '',
    errorSecondaryMessage: emailSubscriptionFields?.errorSecondaryMessage?.value || '',
    successMessage: usePlainTextWrapper({
      field: emailSubscriptionFields?.successMessage,
      tokens: [
        {
          key: '${subscribedToUnsubscribedFromSuccessText}',
          value: isSubscribed
            ? getDictionaryValue('Components.MyAccount.SubscribedTo')?.toLowerCase()
            : getDictionaryValue('Components.MyAccount.UnsubscribedFrom')?.toLowerCase(),
        },
      ],
    }),
    subscribeButtonText: usePlainTextWrapper({
      field: emailSubscriptionFields?.subscribeButtonText,
    }),
    subscribeButtonAriaLabel: emailSubscriptionFields?.subscribeButtonText?.value,
    unsubscribeButtonText: usePlainTextWrapper({
      field: emailSubscriptionFields?.unsubscribeButtonText,
    }),
    unsubscribeButtonAriaLabel: emailSubscriptionFields?.unsubscribeButtonText?.value,
    isSubscribed: !!isSubscribed,
    newsLetterSubscriptionId,
  };
};

const PersonalInformationTabFields = ({
  personalInformationFields,
  enableQuestionsDropdown,
}: {
  personalInformationFields: UserProfile.MyAccount.PersonalInformation['fields'] | undefined;
  enableQuestionsDropdown: boolean;
}): TabFieldMap | undefined => {
  return {
    headline: usePlainTextWrapper({ field: personalInformationFields?.headline }),
    description: usePlainTextWrapper({ field: personalInformationFields?.description }),
    editHeadline: usePlainTextWrapper({ field: personalInformationFields?.editHeadline }),
    editDescription: usePlainTextWrapper({ field: personalInformationFields?.editDescription }),
    emailLabel: usePlainTextWrapper({ field: personalInformationFields?.emailLabel }),
    zipCodeLabel: usePlainTextWrapper({ field: personalInformationFields?.zipCodeLabel }),
    emailUpdateLabel: usePlainTextWrapper({ field: personalInformationFields?.emailUpdateLabel }),
    zipCodeUpdateLabel: usePlainTextWrapper({
      field: personalInformationFields?.zipCodeUpdateLabel,
    }),
    questionLabel: usePlainTextWrapper({ field: personalInformationFields?.questionLabel }),
    questionPlaceholder: enableQuestionsDropdown
      ? personalInformationFields?.questionPlaceholder?.value
      : undefined,
    questionId: enableQuestionsDropdown ? personalInformationFields?.questionId?.value : undefined,
    questionOptions: enableQuestionsDropdown
      ? getQuestionOptions(personalInformationFields?.questionsDropdownList)
      : undefined,
    saveButtonText: usePlainTextWrapper({ field: personalInformationFields?.saveButton }),
    cancelButtonText: usePlainTextWrapper({ field: personalInformationFields?.cancelButton }),
    leavePageTitle: usePlainTextWrapper({ field: personalInformationFields?.confirmationTitle }),
    leavePageDescription: usePlainTextWrapper({
      field: personalInformationFields?.confirmationDescription,
    }),
    keepEditingButtonText: personalInformationFields?.keepEditingText?.value,
    leaveButtonText: personalInformationFields?.submitButtonText?.value,
  };
};

// Helper component to convert a link to a TabItem
const TabItemConverter = ({
  link,
  passwordFields,
  personalInformationFields,
  emailSubscriptionFields,
  session,
  siteSettings,
  getDictionaryValue,
}: {
  link: Item;
  passwordFields?: UserProfile.MyAccount.Password['fields'];
  personalInformationFields?: UserProfile.MyAccount.PersonalInformation['fields'];
  emailSubscriptionFields?: UserProfile.MyAccount.EmailSubscriptions['fields'];
  session: Session | null;
  siteSettings: SiteSettings | null;
  getDictionaryValue: (key: string) => string;
}): TabItem | null => {
  const passwordTabFields = passwordFields ? PasswordTabFields({ passwordFields }) : undefined;
  const loginRegistrationSettings =
    siteSettings?.loginRegistrationSettings as Data.Settings.LoginAndRegistrationSettings;
  const loginRegFields = loginRegistrationSettings?.fields as
    | Record<string, Field<boolean>>
    | undefined;
  const enableQuestionsDropdown = loginRegFields?.enableQuestionsDropdown?.value || false;
  const personalInformationTabFields = personalInformationFields
    ? PersonalInformationTabFields({ personalInformationFields, enableQuestionsDropdown })
    : undefined;
  const emailSubscriptionTabFields = emailSubscriptionFields
    ? EmailSubscriptionTabFields({
        emailSubscriptionFields,
        session,
        siteSettings,
        getDictionaryValue,
      })
    : undefined;

  // Access linkType as Item and get its name
  const linkType = link.fields?.linkType as Item | undefined;
  const linkTypeName = linkType?.name;
  const labelNode = usePlainTextWrapper({
    field: (link.fields?.title as TextField | undefined) || { value: linkTypeName },
  });

  if (!linkTypeName) return null;

  // Map link type names to tab IDs
  let tabId: TabId | null = null;
  if (linkTypeName === 'Password') {
    tabId = 'password';
  } else if (linkTypeName === 'Personal Information') {
    tabId = 'personal-information';
  } else if (linkTypeName === 'Email Subscription') {
    tabId = 'email-subscription';
  }

  if (!tabId) return null;

  // Extract tab-specific fields based on tab type
  let tabFields: TabFieldMap | undefined;

  if (tabId === 'password') {
    tabFields = passwordTabFields;
  }

  if (tabId === 'personal-information') {
    tabFields = personalInformationTabFields;
  }

  if (tabId === 'email-subscription') {
    tabFields = emailSubscriptionTabFields;
  }

  return {
    id: tabId,
    labelNode,
    fields: tabFields,
  } as TabItem;
};

const MyAccountSitecore = (props: MyAccountSitecoreProps): JSX.Element => {
  // Sitecore editing mode (Experience Editor/Pages)
  const isPageEditing = useIsEditing();

  const { data: session } = useSessionSafe();
  const siteSettings = useSiteSettings();
  const { getDictionaryValue } = useDictionary();

  // Convert headline to ReactNode
  const headlineNode = usePlainTextWrapper({ field: props.fields?.title, tag: 'h1' });

  // Extract myAccountLinks and build tabs
  const myAccountLinks = props.fields?.myAccountLinks || [];

  // Find password link to extract fields
  const passwordLink = myAccountLinks.find((link) => {
    const linkType = link.fields?.linkType as Item | undefined;
    return linkType?.name === 'Password';
  });

  // Find email subscription link to extract fields
  const emailSubscriptionLink = myAccountLinks.find((link) => {
    const linkType = link.fields?.linkType as Item | undefined;
    return linkType?.name === 'Email Subscription';
  });

  const personalInformationLink = myAccountLinks.find((link) => {
    const linkType = link.fields?.linkType as Item | undefined;
    return linkType?.name === 'Personal Information';
  });

  const passwordFields = passwordLink?.fields as
    | UserProfile.MyAccount.Password['fields']
    | undefined;

  const emailSubscriptionFields = emailSubscriptionLink?.fields as
    | UserProfile.MyAccount.EmailSubscriptions['fields']
    | undefined;

  const personalInformationFields = personalInformationLink?.fields as
    | UserProfile.MyAccount.PersonalInformation['fields']
    | undefined;

  const tabs: TabItem[] = myAccountLinks
    .map((link) =>
      TabItemConverter({
        link,
        passwordFields,
        personalInformationFields,
        emailSubscriptionFields,
        session,
        siteSettings,
        getDictionaryValue,
      })
    )
    .filter((tab): tab is TabItem => tab !== null);

  // Convert logout text to ReactNode
  const logoutTextNode = usePlainTextWrapper({ field: props.fields?.logoutText });

  const phKey = props.params?.DynamicPlaceholderId
    ? `custom-headless-sidebar-${props.params?.DynamicPlaceholderId}`
    : 'custom-headless-sidebar';

  // Create PlaceholderWrapper for card components
  const cardComponent = <PlaceholderWrapper name={phKey} rendering={props.rendering} />;

  const externalLinks = props.fields?.externalLinks?.map(
    (link: UserProfile.MyAccount.ExternalLinks, index: number) => (
      <LinkWrapper
        key={index}
        field={link.fields?.externalLink}
        ctaStyle="link"
        ctaVariant="brand"
        ctaIconSize="sm"
      />
    )
  );

  return (
    <MyAccount
      headlineNode={headlineNode}
      tabs={tabs.length > 0 ? tabs : undefined}
      externalLinks={externalLinks}
      logoutTextNode={logoutTextNode}
      cta={useLinkWrapper({
        field: props.fields?.cta,
        ctaStyle: 'filled',
        ctaVariant: 'brand',
        ctaIconSize: 'sm',
      })}
      cardComponent={cardComponent}
      isPageEditing={isPageEditing}
      siteSettings={siteSettings}
    />
  );
};

export default MyAccountSitecore;
